import { useState, useEffect } from 'react'
import { getAdminServices, addService, updateService, deleteService, restoreService } from '../services/dbService'
import DEFAULT_SERVICES from '../data/services'

const initialFormState = {
  title: '', icon: 'fas fa-hard-hat', shortDescription: '', fullDescription: '', 
  image: '', slug: '', linkOverride: '', featured: false,
  benefits: [], process: [], locations: [], faqs: []
}

export default function AdminServices() {
  const [services, setServices] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showForm, setShowForm] = useState(false)

  const [editingId, setEditingId] = useState(null)
  const [formData, setFormData] = useState(initialFormState)
  const [formLoading, setFormLoading] = useState(false)

  const fetchServices = async () => {
    setLoading(true)
    setError('')
    try {
      const data = await getAdminServices()
      setServices(data)
    } catch (err) {
      setError('Failed to fetch services. ' + err.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchServices()
  }, [])

  const generateSlug = (title) => {
    return title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)+/g, '') + '-delhi-ncr'
  }

  const resetForm = () => {
    setFormData(initialFormState)
    setEditingId(null)
    setShowForm(false)
  }

  const handleEdit = (service) => {
    setFormData({
      title: service.title || '',
      icon: service.icon || 'fas fa-hard-hat',
      shortDescription: service.shortDescription || '',
      fullDescription: service.fullDescription || '',
      image: service.image || '',
      slug: service.slug || '',
      linkOverride: service.linkOverride || '',
      featured: service.featured || false,
      benefits: service.benefits || [],
      process: service.process || [],
      locations: service.locations || [],
      faqs: service.faqs || []
    })
    setEditingId(service.id)
    setShowForm(true)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!formData.title || !formData.shortDescription || !formData.fullDescription) {
      alert('Title, Short Description, and Full Description are required.')
      return
    }

    const data = {
      ...formData,
      slug: formData.slug || generateSlug(formData.title)
    }

    setFormLoading(true)
    setError('')
    try {
      if (editingId) {
        await updateService(editingId, data)
      } else {
        await addService(data)
      }
      resetForm()
      await fetchServices()
    } catch (err) {
      setError('Failed to save service. ' + err.message)
    } finally {
      setFormLoading(false)
    }
  }

  const handleDelete = async (id, title) => {
    if (window.confirm(`Are you sure you want to delete "${title}"?`)) {
      try {
        await deleteService(id, false)
        await fetchServices()
      } catch (err) {
        setError('Failed to delete service. ' + err.message)
      }
    }
  }

  const handleRestore = async (id) => {
    try {
      await restoreService(id)
      await fetchServices()
    } catch (err) {
      setError('Failed to restore service. ' + err.message)
    }
  }

  const handleLoadDefaults = async () => {
    if (window.confirm('This will insert all 6 default services into your database. Proceed?')) {
      setLoading(true)
      try {
        for (const s of DEFAULT_SERVICES) {
          // Remove arbitrary id, letting Firestore generate one
          const { id, ...serviceData } = s
          await addService(serviceData)
        }
        await fetchServices()
      } catch (err) {
        setError('Failed to load default services. ' + err.message)
      } finally {
        setLoading(false)
      }
    }
  }

  // --- Dynamic Array Handlers ---
  const handleArrayChange = (field, index, value) => {
    const newArr = [...formData[field]]
    newArr[index] = value
    setFormData({ ...formData, [field]: newArr })
  }
  const addArrayItem = (field, defaultVal) => setFormData({ ...formData, [field]: [...formData[field], defaultVal] })
  const removeArrayItem = (field, index) => {
    const newArr = [...formData[field]]
    newArr.splice(index, 1)
    setFormData({ ...formData, [field]: newArr })
  }

  const handleObjectArrayChange = (field, index, key, value) => {
    const newArr = [...formData[field]]
    newArr[index] = { ...newArr[index], [key]: value }
    setFormData({ ...formData, [field]: newArr })
  }

  if (loading) return <div>Loading services...</div>

  const ICON_OPTIONS = [
    'fas fa-hard-hat', 'fas fa-drafting-compass', 'fas fa-couch', 'fas fa-warehouse',
    'fas fa-tasks', 'fas fa-map-marked-alt', 'fas fa-building', 'fas fa-home',
    'fas fa-paint-roller', 'fas fa-hammer', 'fas fa-ruler-combined', 'fas fa-tools'
  ]

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
        <h2 style={{ fontSize: '1.2rem', color: '#7a8fa6', margin: 0 }}>Manage Services ({services.length})</h2>
        <button onClick={() => { resetForm(); setShowForm(true) }} className="btn-gold" style={{ padding: '8px 16px', fontSize: '0.9rem' }}>
          + Add Service
        </button>
      </div>

      {error && <div style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', padding: 12, borderRadius: 8, marginBottom: 20 }}>{error} <button onClick={fetchServices} style={{ background: 'transparent', border: '1px solid currentColor', color: 'inherit', padding: '2px 8px', borderRadius: 4, marginLeft: 10, cursor: 'pointer' }}>Retry</button></div>}

      {showForm && (
        <div style={{ background: 'rgba(255,255,255,0.05)', padding: 24, borderRadius: 12, marginBottom: 30 }}>
          <h3 style={{ marginTop: 0, marginBottom: 20 }}>{editingId ? 'Edit Service' : 'New Service'}</h3>
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            
            {/* Core Info */}
            <div style={{ padding: '16px', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8 }}>
              <h4 style={{ margin: '0 0 12px 0', color: '#f5c518' }}>Basic Information</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16, marginBottom: 16 }}>
                <input type="text" placeholder="Service Title *" value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} required style={inputStyle} />
                <select value={formData.icon} onChange={e => setFormData({...formData, icon: e.target.value})} style={inputStyle}>
                  {ICON_OPTIONS.map(icon => (
                    <option key={icon} value={icon}>{icon.replace('fas fa-', '').replace(/-/g, ' ')}</option>
                  ))}
                </select>
              </div>

              <input type="url" placeholder="Hero Image URL (optional)" value={formData.image} onChange={e => setFormData({...formData, image: e.target.value})} style={{...inputStyle, marginBottom: 16}} />
              {formData.image && (
                <div style={{ marginBottom: 16 }}>
                  <img src={formData.image} alt="Preview" onError={(e) => { e.target.src = 'https://via.placeholder.com/400x200?text=Invalid+Image+URL' }} style={{ height: 100, objectFit: 'cover', borderRadius: 8 }} />
                </div>
              )}

              <textarea placeholder="Short Description (shown on card) *" maxLength={200} rows={2} value={formData.shortDescription} onChange={e => setFormData({...formData, shortDescription: e.target.value})} required style={{...inputStyle, marginBottom: 16}} />
              <textarea placeholder="Full Description (shown on page) * — Use new lines for paragraphs, start lines with • for bullet points" maxLength={2000} rows={6} value={formData.fullDescription} onChange={e => setFormData({...formData, fullDescription: e.target.value})} required style={{...inputStyle, marginBottom: 16}} />
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, alignItems: 'center' }}>
                <input type="text" placeholder="URL Slug (auto-generated if empty)" value={formData.slug} onChange={e => setFormData({...formData, slug: e.target.value})} style={inputStyle} />
                <input type="text" placeholder="Link Override (e.g. #properties) (optional)" value={formData.linkOverride} onChange={e => setFormData({...formData, linkOverride: e.target.value})} style={inputStyle} />
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginTop: 12 }}>
                <input type="checkbox" checked={formData.featured} onChange={e => setFormData({...formData, featured: e.target.checked})} />
                Featured Service (Shows "Most Popular" badge)
              </label>
            </div>

            {/* Arrays Section */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              {/* Benefits */}
              <div style={{ padding: '16px', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8 }}>
                 <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                   <h4 style={{ margin: 0, color: '#f5c518' }}>Why Choose Us (Benefits)</h4>
                   <button type="button" onClick={() => addArrayItem('benefits', '')} style={smallAddBtn}>+ Add</button>
                 </div>
                 {formData.benefits.map((b, i) => (
                   <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                     <input type="text" value={b} onChange={e => handleArrayChange('benefits', i, e.target.value)} placeholder={`Benefit ${i+1}`} style={{...inputStyle, padding: '8px 12px'}} />
                     <button type="button" onClick={() => removeArrayItem('benefits', i)} style={removeBtn}>×</button>
                   </div>
                 ))}
                 {formData.benefits.length === 0 && <p style={{ fontSize: '0.85rem', color: '#7a8fa6' }}>No benefits added.</p>}
              </div>

              {/* Service Areas */}
              <div style={{ padding: '16px', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8 }}>
                 <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                   <h4 style={{ margin: 0, color: '#f5c518' }}>Service Areas</h4>
                   <button type="button" onClick={() => addArrayItem('locations', '')} style={smallAddBtn}>+ Add</button>
                 </div>
                 {formData.locations.map((loc, i) => (
                   <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                     <input type="text" value={loc} onChange={e => handleArrayChange('locations', i, e.target.value)} placeholder={`Location ${i+1}`} style={{...inputStyle, padding: '8px 12px'}} />
                     <button type="button" onClick={() => removeArrayItem('locations', i)} style={removeBtn}>×</button>
                   </div>
                 ))}
                 {formData.locations.length === 0 && <p style={{ fontSize: '0.85rem', color: '#7a8fa6' }}>No locations added.</p>}
              </div>
            </div>

            {/* Process */}
            <div style={{ padding: '16px', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8 }}>
               <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                 <h4 style={{ margin: 0, color: '#f5c518' }}>Our Process Steps</h4>
                 <button type="button" onClick={() => addArrayItem('process', {step: '', desc: ''})} style={smallAddBtn}>+ Add Step</button>
               </div>
               {formData.process.map((p, i) => (
                 <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'flex-start' }}>
                   <div style={{ width: 24, height: 24, borderRadius: '50%', background: 'rgba(245,197,24,0.2)', color: '#f5c518', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 10, fontSize: '0.8rem', fontWeight: 'bold' }}>{i+1}</div>
                   <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                     <input type="text" value={p.step} onChange={e => handleObjectArrayChange('process', i, 'step', e.target.value)} placeholder="Step Title (e.g. Site Inspection)" style={{...inputStyle, padding: '8px 12px'}} />
                     <textarea value={p.desc} onChange={e => handleObjectArrayChange('process', i, 'desc', e.target.value)} placeholder="Step Description" rows={2} style={{...inputStyle, padding: '8px 12px'}} />
                   </div>
                   <button type="button" onClick={() => removeArrayItem('process', i)} style={{...removeBtn, marginTop: 10}}>×</button>
                 </div>
               ))}
               {formData.process.length === 0 && <p style={{ fontSize: '0.85rem', color: '#7a8fa6' }}>No process steps added.</p>}
            </div>

            {/* FAQs */}
            <div style={{ padding: '16px', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8 }}>
               <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                 <h4 style={{ margin: 0, color: '#f5c518' }}>FAQs (Generates SEO Schema)</h4>
                 <button type="button" onClick={() => addArrayItem('faqs', {q: '', a: ''})} style={smallAddBtn}>+ Add FAQ</button>
               </div>
               {formData.faqs.map((f, i) => (
                 <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'flex-start' }}>
                   <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                     <input type="text" value={f.q} onChange={e => handleObjectArrayChange('faqs', i, 'q', e.target.value)} placeholder="Question" style={{...inputStyle, padding: '8px 12px', background: 'rgba(255,255,255,0.05)'}} />
                     <textarea value={f.a} onChange={e => handleObjectArrayChange('faqs', i, 'a', e.target.value)} placeholder="Answer" rows={2} style={{...inputStyle, padding: '8px 12px'}} />
                   </div>
                   <button type="button" onClick={() => removeArrayItem('faqs', i)} style={removeBtn}>×</button>
                 </div>
               ))}
               {formData.faqs.length === 0 && <p style={{ fontSize: '0.85rem', color: '#7a8fa6' }}>No FAQs added.</p>}
            </div>

            <div style={{ display: 'flex', gap: 12, marginTop: 10 }}>
              <button type="submit" disabled={formLoading} className="btn-gold" style={{ padding: '12px 30px', fontSize: '1rem' }}>
                {formLoading ? 'Saving...' : 'Save Service'}
              </button>
              <button type="button" onClick={resetForm} disabled={formLoading} style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: 'white', padding: '12px 30px', borderRadius: 8, cursor: 'pointer', fontSize: '1rem' }}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {services.length === 0 ? (
        <div style={{ background: 'rgba(255,255,255,0.03)', padding: 40, borderRadius: 12, textAlign: 'center', color: '#7a8fa6' }}>
          <i className="far fa-folder-open fa-3x" style={{ opacity: 0.3, marginBottom: 16 }} />
          <br/>No custom services found in your database.
          <br/><br/>
          <button onClick={handleLoadDefaults} className="btn-gold" style={{ padding: '12px 24px', fontSize: '1rem', cursor: 'pointer', borderRadius: '8px' }}>
            <i className="fas fa-cloud-download-alt" style={{ marginRight: 8 }}></i> Import All Default Services
          </button>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 20 }}>
          {services.map(s => (
            <div key={s.id} style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 8, overflow: 'hidden', border: s.isDeleted ? '1px dashed #ef4444' : '1px solid transparent', opacity: s.isDeleted ? 0.7 : 1 }}>
              {s.image && <img src={s.image} alt={s.title} onError={(e) => { e.target.src = 'https://via.placeholder.com/400x200?text=Image+Error' }} style={{ width: '100%', height: 140, objectFit: 'cover' }} />}
              <div style={{ padding: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                  <h4 style={{ margin: 0, fontSize: '1.05rem' }}>
                    <i className={s.icon} style={{ color: '#f5c518', marginRight: 8, fontSize: '0.9rem' }} />
                    {s.title} {s.featured && <i className="fas fa-star" style={{ color: '#f5c518', fontSize: '0.7rem' }} />}
                  </h4>
                </div>
                <p style={{ fontSize: '0.85rem', color: '#7a8fa6', marginBottom: 12, lineHeight: 1.5 }}>{s.shortDescription?.substring(0, 80)}...</p>
                {s.isDeleted && <div style={{ color: '#ef4444', fontSize: '0.8rem', fontWeight: 'bold', marginBottom: 10 }}>[DELETED - TRASH]</div>}
                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  <button onClick={() => handleEdit(s)} style={actionBtnStyle}>Edit</button>
                  {s.isDeleted ? (
                    <button onClick={() => handleRestore(s.id)} style={{...actionBtnStyle, color: '#4ade80', borderColor: 'rgba(34,197,94,0.3)'}}>Restore</button>
                  ) : (
                    <button onClick={() => handleDelete(s.id, s.title)} style={{...actionBtnStyle, color: '#f87171', borderColor: 'rgba(239,68,68,0.3)'}}>Delete</button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

const inputStyle = {
  width: '100%', padding: '12px 16px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(0,0,0,0.2)', color: 'white', fontFamily: "'Inter', sans-serif"
}

const actionBtnStyle = {
  flex: 1, background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: 'white', padding: '6px 0', borderRadius: 6, cursor: 'pointer', fontSize: '0.85rem', textTransform: 'uppercase', fontWeight: 600
}

const smallAddBtn = {
  background: 'rgba(245,197,24,0.1)', color: '#f5c518', border: '1px solid rgba(245,197,24,0.3)', borderRadius: 4, padding: '4px 8px', fontSize: '0.8rem', cursor: 'pointer', fontWeight: 'bold'
}

const removeBtn = {
  background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: 4, padding: '0 12px', fontSize: '1.2rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center'
}
